import { User } from '@flowwai-frontend/bps-officer-web/graphql'
import { useAsyncWithAuth } from '@flowwai-frontend/shared/auth/common'
import {
   convertToUserType,
   convertUserGoogleAuthConsentStatusToUserGoogleCalenderAuthConsentStatusType,
   getUserRoleIdsForGqlIdentifications,
   getUserRoleNamesForGqlIdentifications,
   updateUserPermissions
} from '../../factories'

import {
   GetCurrentUserProfileQueryResult,
   GetCurrentUserProfileQueryVariables
} from '../../query/GetCurrentUserDetails/query.generatedTypes'

import { useUserService } from '../services/useUserService'
import { useUserAppConfigStore } from '../stores/useUserAppConfigStore'

const useGetCurrentUserDetails = () => {
   const userService = useUserService()
   const userStore = useUserAppConfigStore()

   const { triggerAPI, apiStatus, error: apiError } = useAsyncWithAuth<
      GetCurrentUserProfileQueryResult | unknown,
      Error,
      GetCurrentUserProfileQueryVariables
   >(userService.getUserProfile)

   const onSuccessAPI = (
      response: GetCurrentUserProfileQueryResult | unknown
   ): void => {
      if (typeof response !== 'object') return
      if (!response) return
      if (!('data' in response)) return

      const { data } = response as GetCurrentUserProfileQueryResult
      if (!data) return

      const user = data.getUserProfile as User
      if (!user) return

      userStore.createOrUpdateUserDetails(convertToUserType(user))
      const userPermissionsModel = updateUserPermissions(
         user,
         userStore.permissions
      )
      userStore.setPermissions(userPermissionsModel)
      userStore.setUserGoogleCalenderAuthConsentStatus(
         convertUserGoogleAuthConsentStatusToUserGoogleCalenderAuthConsentStatusType(
            user.googleAuthConsentStatus
         )
      )

      const userModel = userStore.userDetails

      const userRoleIds = getUserRoleIdsForGqlIdentifications(user)
      const userRoleNames = getUserRoleNamesForGqlIdentifications(user)

      userModel?.setRoleIds(userRoleIds)
      userModel?.setRoleNames(userRoleNames)
   }

   const doNetworkCall = (
      onSuccess?: () => void,
      onFailure?: (error: Error) => void
   ): void => {
      triggerAPI(
         {},
         {
            onSuccess: (
               response: GetCurrentUserProfileQueryResult | unknown
            ) => {
               onSuccessAPI(response)
               if (response && onSuccess) {
                  onSuccess()
               }
            },
            onError: onFailure
         }
      )
   }

   return {
      getUserProfileAPITrigger: doNetworkCall,
      getUserProfileAPIStatus: apiStatus,
      getUserProfileAPIError: apiError
   }
}

export { useGetCurrentUserDetails }
